// ============================================================================
// "Reconcile Zoho docs with billing address" — check, then repair, one order's
// billing identity in Zoho.
//
// Not an address change: there is nothing to fill in. The billing address on the
// estimate already decides who is billed — its GSTIN means a B2B contact, no
// GSTIN means B2C — and this shows whether every live Zoho document agrees with
// it, then forces the ones that do not.
//
// The case it exists for is a conversion that half-landed — the sales order
// re-pointed and the invoice's PUT lost to a 502 — which leaves the customer
// holding an invoice on the wrong contact while the order looks converted. The
// legacy retry cannot fix it: it refuses a retrofit row reading `completed`,
// which is exactly what a half-landed conversion leaves behind.
//
// Opened from the order's Actions menu, which applies the permission filter, so
// this renders no trigger and no permission check of its own.
// ============================================================================
import { SyncOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import React, { useCallback, useEffect, useState } from "react";
import {
  BillingDrift,
  BillingDriftDoc,
  BillingDriftSettlement,
  movePaymentContact,
  fetchBillingDrift,
  reconcileBilling,
} from "../oms.api";

const rupees = (v: number) => `₹${Number(v || 0).toFixed(2)}`;

// The settlement read: WHERE THE MONEY IS. Shown here because this is the screen an operator opens
// when an order wears the amber "Invoice Error", and the document table above can be entirely green
// while the invoice still cannot settle — the pre-ship gate searches payments scoped to the contact
// the estimate resolves to now, so a payment on another contact is invisible to it. Saying "every
// document is correct" and stopping is what sends someone round the loop of pressing Re-sync and
// watching nothing change.
//
// `onMove` is offered only for money that is genuinely misplaced, and it is NOT gated on the
// re-sync's `blocking_reasons` — see the note on `movePaymentContact`.
const SettlementNotice: React.FC<{
  s: BillingDriftSettlement;
  onMove: () => void;
  moving: boolean;
}> = ({ s, onMove, moving }) => {
  const misplaced = s.payments.filter((p) => p.on_expected_contact === false);
  const missing = s.payments.filter((p) => !p.in_zoho);
  const balance = s.unpaid_invoices.reduce((t, i) => t + i.balance, 0);

  // Ordered by what the operator should act on: money in the wrong place is fixable right here,
  // money that never arrived is the Cash Log's re-push, and neither means the shortfall is real.
  const message =
    misplaced.length > 0
      ? "The money for this order is on a different Zoho contact"
      : missing.length > 0
      ? "Zoho holds no payment under this order's reference(s)"
      : `Invoice not settled in Zoho — ${rupees(balance)} outstanding`;

  return (
    <Alert
      type="warning"
      showIcon
      style={{ marginBottom: 12 }}
      message={message}
      description={
        <Space direction="vertical" size={4} style={{ fontSize: 12 }}>
          {s.unpaid_invoices.map((i) => (
            <div key={i.zoho_doc_id}>
              Invoice {i.number || i.zoho_doc_id}: {rupees(i.balance)} outstanding
            </div>
          ))}

          {s.payments.map((p) => (
            <div key={p.reference}>
              Payment <Typography.Text code>{p.reference}</Typography.Text>:{" "}
              {!p.in_zoho ? (
                <Typography.Text type="danger">not in Zoho</Typography.Text>
              ) : (
                <>
                  {rupees(p.amount)} on {p.customer_name || p.customer_id}
                  {p.unused_amount > 0 && ` (${rupees(p.unused_amount)} unused)`}{" "}
                  {p.on_expected_contact === false ? (
                    <Tag color="red">wrong contact</Tag>
                  ) : p.on_expected_contact === null ? (
                    <Tag>contact not checked</Tag>
                  ) : (
                    <Tag color="green">right contact</Tag>
                  )}
                </>
              )}
            </div>
          ))}

          {misplaced.length > 0 && (
            <Popconfirm
              title="Move the payment to the right contact?"
              description={
                <div style={{ maxWidth: 380 }}>
                  Zoho cannot re-point a payment, so each one is <b>deleted and re-recorded</b>{" "}
                  against the contact this estimate bills — same amount, same payment date, same
                  reference. The invoice is then re-checked so the money applies and the hold
                  clears. A payment that could not be re-created afterwards is refused up front
                  rather than deleted.
                </div>
              }
              okText="Move it"
              onConfirm={onMove}
              disabled={moving}
            >
              <Button size="small" type="primary" danger loading={moving}>
                Move payment to the right contact
              </Button>
            </Popconfirm>
          )}
          {misplaced.length === 0 && missing.length > 0 && (
            <Typography.Text type="secondary">
              The capture-time push never landed. Re-push it from Cash Log &rsaquo; Payments
              &rsaquo; Zoho reconcile — this screen only moves a payment that already exists.
            </Typography.Text>
          )}
          {misplaced.length === 0 && missing.length === 0 && (
            <Typography.Text type="secondary">
              Every payment we know of is on the right contact, so this is a genuine balance —
              a few paise is the rounding artefact (use &ldquo;Settle invoice rounding&rdquo; on
              the demand line), anything larger is money still to collect.
            </Typography.Text>
          )}

          {/* An unread payment is not evidence of a healthy one, so it is stated rather than
              folded into the counts above. */}
          {s.unreadable.map((u) => (
            <Typography.Text key={u} type="secondary">
              Could not be checked: {u}
            </Typography.Text>
          ))}
          {s.truncated > 0 && (
            <Typography.Text type="secondary">
              {s.truncated} further payment reference(s) were not checked.
            </Typography.Text>
          )}
        </Space>
      }
    />
  );
};

const KIND_LABEL: Record<string, string> = {
  sales_order: "Sales order",
  invoice: "Invoice",
  delivery_challan: "Delivery challan",
};

const FIELD_LABEL: Record<string, string> = {
  gst_no: "GSTIN",
  gst_treatment: "GST treatment",
  customer_id: "Zoho contact",
};

const columns = [
  {
    title: "Document",
    key: "doc",
    render: (_: unknown, d: BillingDriftDoc) => (
      <Space direction="vertical" size={0}>
        <Typography.Text>{KIND_LABEL[d.kind] || d.kind}</Typography.Text>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {d.number || d.zoho_doc_id}
        </Typography.Text>
      </Space>
    ),
  },
  {
    title: "Billed to",
    key: "billed",
    render: (_: unknown, d: BillingDriftDoc) => (
      <Space direction="vertical" size={0}>
        <Typography.Text>{d.customer_name || "—"}</Typography.Text>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {d.gst_no || "no GSTIN (B2C)"}
        </Typography.Text>
      </Space>
    ),
  },
  {
    title: "State",
    key: "state",
    render: (_: unknown, d: BillingDriftDoc) =>
      d.in_sync ? (
        <Tag color="green">Correct</Tag>
      ) : (
        <Space direction="vertical" size={2}>
          <Tag color="red">Wrong</Tag>
          {d.wrong_fields.map((w) => (
            <Typography.Text key={w.field} style={{ fontSize: 12 }}>
              {FIELD_LABEL[w.field] || w.field}:{" "}
              <Typography.Text type="danger">{w.actual || "—"}</Typography.Text> →{" "}
              <Typography.Text type="success">{w.expected || "—"}</Typography.Text>
            </Typography.Text>
          ))}
        </Space>
      ),
  },
];

const BillingReconcileModal: React.FC<{
  salesOrderId: number;
  open: boolean;
  onClose: () => void;
}> = ({ salesOrderId, open, onClose }) => {
  const [drift, setDrift] = useState<BillingDrift | undefined>();
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [moving, setMoving] = useState(false);

  // The check reads every one of the order's documents from Zoho, so it runs only when the action
  // is actually picked — never on a detail-page render. Re-read on each open rather than cached:
  // the answer moves as the order's documents do.
  const load = useCallback(async () => {
    setLoading(true);
    try {
      setDrift(await fetchBillingDrift(salesOrderId));
    } finally {
      setLoading(false);
    }
  }, [salesOrderId]);

  useEffect(() => {
    if (open) load();
    else setDrift(undefined);
  }, [open, load]);

  const onRun = async () => {
    setRunning(true);
    try {
      const after = await reconcileBilling(salesOrderId);
      if (after) setDrift(after);
    } finally {
      setRunning(false);
    }
  };

  // Re-read after the move so the notice reflects what actually happened — including the case where
  // the money moved but the invoice has not been re-checked yet.
  const onMove = async () => {
    setMoving(true);
    try {
      await movePaymentContact(salesOrderId);
      await load();
    } finally {
      setMoving(false);
    }
  };

  const blocked = drift?.blocking_reasons?.length ? drift.blocking_reasons : undefined;
  const expected = drift?.expected;

  return (
      <Modal
        title="Reconcile Zoho docs with billing address"
        open={open}
        onCancel={onClose}
        width={760}
        destroyOnClose
        footer={
          <Space>
            <Button onClick={onClose}>Close</Button>
            <Button icon={<SyncOutlined />} onClick={load} loading={loading}>
              Re-check
            </Button>
            <Popconfirm
              title="Re-sync Zoho billing?"
              description={
                <div style={{ maxWidth: 360 }}>
                  Forces this order&rsquo;s Zoho documents onto the billing identity the
                  estimate already names. Changes no address and no amount. Safe to
                  repeat — documents that are already correct are left untouched.
                </div>
              }
              okText="Re-sync"
              onConfirm={onRun}
              disabled={!!blocked || running}
            >
              <Button
                type="primary"
                danger={!drift?.in_sync}
                loading={running}
                disabled={!drift || !!blocked}
              >
                Re-sync now
              </Button>
            </Popconfirm>
          </Space>
        }
      >
        {blocked && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 12 }}
            message="This order cannot be re-billed"
            description={blocked.map((r) => (
              <div key={r}>{r}</div>
            ))}
          />
        )}

        {drift && !blocked && (
          <Alert
            type={drift.in_sync ? "success" : "warning"}
            showIcon
            style={{ marginBottom: 12 }}
            message={
              drift.in_sync
                ? "Every Zoho document bills the right identity"
                : `${drift.out_of_sync_count} document(s) bill the wrong identity`
            }
            description={
              expected && (
                <Space direction="vertical" size={0}>
                  <span>
                    Should be{" "}
                    <Tag color={expected.billing === "b2b" ? "blue" : "default"}>
                      {expected.billing.toUpperCase()}
                    </Tag>
                    {expected.gstin ? (
                      <Typography.Text code>{expected.gstin}</Typography.Text>
                    ) : (
                      "no GSTIN"
                    )}
                  </span>
                  {/* The contact could not be checked, so a green tick here covers the GST fields
                      only — say so rather than implying the contact was verified. */}
                  {!drift.contact_checked && (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      No Zoho contact is cached for this customer yet, so the contact
                      itself was not compared — only the GST fields.
                    </Typography.Text>
                  )}
                </Space>
              )
            }
          />
        )}

        {/* Below the document verdict and above the table: a second, separate answer that stays on
            screen even when the documents are green — that combination is exactly the case nobody
            could previously explain. Rendered outside the `!blocked` guard on purpose: an invoiced
            order from a previous month cannot be re-billed, but its money can still be moved. */}
        {drift?.settlement && (
          <SettlementNotice s={drift.settlement} onMove={onMove} moving={moving} />
        )}

        <Table<BillingDriftDoc>
          size="small"
          rowKey={(d) => `${d.kind}:${d.zoho_doc_id}`}
          loading={loading}
          columns={columns}
          dataSource={drift?.documents || []}
          pagination={false}
        />

        {!!drift?.unreadable?.length && (
          <Alert
            type="warning"
            showIcon
            style={{ marginTop: 12 }}
            message="Some documents could not be read from Zoho"
            description={drift.unreadable.map((u) => (
              <div key={u.zoho_doc_id}>
                {KIND_LABEL[u.kind] || u.kind} {u.zoho_doc_id} {u.reason}
              </div>
            ))}
          />
        )}
      </Modal>
  );
};

export default BillingReconcileModal;
